import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Card, CardContent } from '@/components/ui/card'
import { type Conversation } from '@/data/mockOrdersData'
import { mockContacts } from '@/data/mockOrdersData'
import { formatSmartTimestamp } from '@/utils/formatTimestamp'
import { MessageCircle } from 'lucide-react'
import { cn } from '@/lib/utils'

interface ConversationListProps {
	conversations: Conversation[]
	selectedConversationId?: string
	onSelectConversation: (conversation: Conversation) => void
	searchQuery?: string
	view?: 'list' | 'grid'
}

export function ConversationList({ 
	conversations, 
	selectedConversationId, 
	onSelectConversation, 
	searchQuery = '',
	view = 'list'
}: ConversationListProps) {
	// Filter conversations based on search
	const filteredConversations = conversations.filter(conversation => {
		if (!searchQuery) return true
		
		const contact = mockContacts.find(c => c.id === conversation.contact_id)
		const lastMessage = conversation.messages[conversation.messages.length - 1]
		
		const searchLower = searchQuery.toLowerCase()
		return (
			contact?.display_name.toLowerCase().includes(searchLower) ||
			contact?.email?.toLowerCase().includes(searchLower) ||
			contact?.phone_raw?.includes(searchQuery) ||
			lastMessage?.message_content?.toLowerCase().includes(searchLower)
		)
	})

	if (filteredConversations.length === 0) {
		return (
			<div className="flex flex-col items-center justify-center h-64 text-center space-y-3">
				<MessageCircle className="h-8 w-8 text-muted-foreground opacity-50" />
				<div>
					<p className="text-sm font-medium text-muted-foreground">
						{searchQuery ? 'No conversations found' : 'No conversations yet'}
					</p>
					<p className="text-xs text-muted-foreground">
						{searchQuery ? 'Try adjusting your search terms' : 'Conversations will appear here when customers message you'}
					</p>
				</div>
			</div>
		)
	}

	if (view === 'grid') {
		return (
			<div className="grid grid-cols-1 gap-3">
				{filteredConversations.map((conversation) => {
					const contact = mockContacts.find(c => c.id === conversation.contact_id)
					const lastMessage = conversation.messages[conversation.messages.length - 1]
					const isSelected = selectedConversationId === conversation.id
					
					// Get message prefix (who sent the last message)
					const getMessagePrefix = () => {
						if (!lastMessage) return ''
						if (lastMessage.direction === 'inbound') {
							// Inbound: use contact's first name
							const firstName = contact?.display_name.split(' ')[0] || 'Contact'
							return `${firstName}: `
						} else {
							// Outbound: use "You" for now (could be actual user name)
							return 'You: '
						}
					}
					const messagePrefix = getMessagePrefix()
					
					return (
						<Card
							key={conversation.id}
							className={cn(
								"cursor-pointer hover:shadow-sm transition-all",
								isSelected && "ring-2 ring-primary bg-muted/80"
							)}
							onClick={() => onSelectConversation(conversation)}
						>
							<CardContent className="p-3">
								<div className="flex items-start gap-3">
									<Avatar className="h-10 w-10 flex-shrink-0">
										<AvatarImage src="" alt={contact?.display_name} />
										<AvatarFallback>
											{contact?.display_name
												.split(' ')
												.map((n) => n[0])
												.join('')
												.toUpperCase()}
										</AvatarFallback>
									</Avatar>
									
									<div className="flex-1 min-w-0">
										<div className="flex items-center justify-between mb-1">
											<div className="font-medium truncate">{contact?.display_name}</div>
											<div className="flex items-center gap-1">
												{conversation.unread_count > 0 && (
													<div className="h-2 w-2 bg-rose-700 rounded-full"></div>
												)}
												<div className="text-xs text-muted-foreground">
													{formatSmartTimestamp(conversation.last_message_at)}
												</div>
											</div>
										</div>
										
										<p className="text-sm text-muted-foreground truncate">
											{messagePrefix}{lastMessage?.message_content || lastMessage?.summary || 'No messages'}
										</p>
									</div>
								</div>
							</CardContent>
						</Card>
					)
				})}
			</div>
		)
	}

	// List view (default)
	return (
		<div className="space-y-1">
			{filteredConversations.map((conversation) => {
				const contact = mockContacts.find(c => c.id === conversation.contact_id)
				const lastMessage = conversation.messages[conversation.messages.length - 1]
				const isSelected = selectedConversationId === conversation.id
				
				// Get message prefix (who sent the last message)
				const getMessagePrefix = () => {
					if (!lastMessage) return ''
					if (lastMessage.direction === 'inbound') {
						// Inbound: use contact's first name
						const firstName = contact?.display_name.split(' ')[0] || 'Contact'
						return `${firstName}: `
					} else {
						// Outbound: use "You" for now (could be actual user name)
						return 'You: '
					}
				}
				const messagePrefix = getMessagePrefix()
				
				return (
					<div
						key={conversation.id}
						className={cn(
							"flex items-start gap-3 p-3 rounded-lg cursor-pointer hover:bg-muted/50 transition-colors",
							isSelected && "bg-muted/80"
						)}
						onClick={() => onSelectConversation(conversation)}
					>
						<Avatar className="h-10 w-10 flex-shrink-0">
							<AvatarImage src="" alt={contact?.display_name} />
							<AvatarFallback>
								{contact?.display_name
									.split(' ')
									.map((n) => n[0])
									.join('')
									.toUpperCase()}
							</AvatarFallback>
						</Avatar>
						
						<div className="flex-1 min-w-0">
							<div className="flex items-center justify-between mb-1">
								<div className="font-medium truncate">{contact?.display_name}</div>
								<div className="flex items-center gap-1">
									{conversation.unread_count > 0 && (
										<div className="h-2 w-2 bg-rose-700 rounded-full"></div>
									)}
									<div className="text-xs text-muted-foreground">
										{formatSmartTimestamp(conversation.last_message_at)}
									</div>
								</div>
							</div>
							
							<p className="text-sm text-muted-foreground truncate">
								{messagePrefix}{lastMessage?.message_content || lastMessage?.summary || 'No messages'}
							</p>
						</div>
					</div>
				)
			})}
		</div>
	)
}
